import type { LeadershipMetrics } from '@delivery-metrics/shared';
import { PieChart } from '@mui/icons-material';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import React from 'react';

import { InteractiveChart } from '../InteractiveChart';

interface EffortDistributionSectionProps {
  data: LeadershipMetrics | null;
  totalEffort: number;
  totalFeatures: number;
}

export const EffortDistributionSection: React.FC<
  EffortDistributionSectionProps
> = ({ data, totalEffort, totalFeatures }) => {
  if (!data) {
    return (
      <Box sx={{ mb: 4 }}>
        <Typography
          variant='h5'
          gutterBottom
          sx={{ display: 'flex', alignItems: 'center', mb: 3 }}
        >
          <PieChart sx={{ mr: 1 }} />
          Effort Distribution & T-Shirt Size Analysis
        </Typography>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card sx={{ height: 400 }}>
              <CardContent>
                <Typography variant='h6'>
                  Loading effort distribution...
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card sx={{ height: 400 }}>
              <CardContent>
                <Typography variant='h6'>
                  Loading T-shirt size distribution...
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
  }

  const effortDistributionData = data.effortDistribution
    ? [
        {
          name: 'New Development',
          value: data.effortDistribution.newDevelopment,
          percentage: data.effortDistribution.newDevelopment.toFixed(1),
        },
        {
          name: 'Maintenance',
          value: data.effortDistribution.maintenance,
          percentage: data.effortDistribution.maintenance.toFixed(1),
        },
        {
          name: 'Technical Debt',
          value: data.effortDistribution.technicalDebt,
          percentage: data.effortDistribution.technicalDebt.toFixed(1),
        },
      ]
    : [];

  const tShirtSizeData = data.tShirtSizeDistribution
    ? Object.entries(data.tShirtSizeDistribution).map(([size, count]) => ({
        name: size,
        value: count,
        percentage:
          totalFeatures > 0 ? ((count / totalFeatures) * 100).toFixed(1) : '0',
      }))
    : [];

  const getTShirtSizeColor = (
    size: string
  ): 'success' | 'info' | 'primary' | 'warning' | 'error' => {
    switch (size) {
      case 'XS':
        return 'success';
      case 'S':
        return 'info';
      case 'M':
        return 'primary';
      case 'L':
        return 'warning';
      case 'XL':
        return 'error';
      default:
        return 'primary';
    }
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Typography
        variant='h5'
        gutterBottom
        sx={{ display: 'flex', alignItems: 'center', mb: 3 }}
      >
        <PieChart sx={{ mr: 1 }} />
        Effort Distribution & T-Shirt Size Analysis
      </Typography>

      <Grid container spacing={3}>
        {/* Effort Distribution Chart */}
        <Grid size={{ xs: 12, md: 6 }}>
          <InteractiveChart
            title='Effort Distribution'
            subtitle='Breakdown of development effort allocation'
            data={effortDistributionData}
            series={[{ key: 'value', name: 'Effort %' }]}
            type='donut'
            height={350}
            colors={['#4caf50', '#ff9800', '#f44336']}
            customTooltip={({ active, payload }) => {
              if (!active || !payload || !payload.length) return null;
              const data = payload[0].payload;
              return (
                <Box
                  sx={{
                    backgroundColor: 'background.paper',
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 1.5,
                    boxShadow: 2,
                  }}
                >
                  <Typography variant='body2' fontWeight='bold'>
                    {data.name}
                  </Typography>
                  <Typography variant='body2'>
                    {data.percentage}% of total effort
                  </Typography>
                </Box>
              );
            }}
          />
        </Grid>

        {/* T-Shirt Size Distribution */}
        <Grid size={{ xs: 12, md: 6 }}>
          <InteractiveChart
            title='T-Shirt Size Distribution'
            subtitle='Feature complexity breakdown'
            data={tShirtSizeData}
            series={[{ key: 'value', name: 'Features' }]}
            type='bar'
            height={350}
            colors={['#e3f2fd', '#bbdefb', '#90caf9', '#64b5f6', '#42a5f5']}
            xAxisKey='name'
            yAxisLabel='Number of Features'
            showLegend={false}
            customTooltip={({ active, payload, label }) => {
              if (!active || !payload || !payload.length) return null;
              const data = payload[0].payload;
              return (
                <Box
                  sx={{
                    backgroundColor: 'background.paper',
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 1.5,
                    boxShadow: 2,
                  }}
                >
                  <Typography variant='body2' fontWeight='bold'>
                    Size {label}
                  </Typography>
                  <Typography variant='body2'>
                    {data.value} features ({data.percentage}%)
                  </Typography>
                </Box>
              );
            }}
          />
        </Grid>
      </Grid>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent>
              <Typography variant='h6' gutterBottom>
                Portfolio Health
              </Typography>
              <Stack spacing={2}>
                <Box>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      mb: 1,
                    }}
                  >
                    <Typography variant='body2'>New Development</Typography>
                    <Typography
                      variant='body2'
                      fontWeight='bold'
                      color='success.main'
                    >
                      {Math.round(data.effortDistribution?.newDevelopment || 0)}
                      %
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant='determinate'
                    value={data.effortDistribution?.newDevelopment || 0}
                    color='success'
                  />
                </Box>
                <Box>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      mb: 1,
                    }}
                  >
                    <Typography variant='body2'>Maintenance</Typography>
                    <Typography
                      variant='body2'
                      fontWeight='bold'
                      color='warning.main'
                    >
                      {Math.round(data.effortDistribution?.maintenance || 0)}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant='determinate'
                    value={data.effortDistribution?.maintenance || 0}
                    color='warning'
                  />
                </Box>
                <Box>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      mb: 1,
                    }}
                  >
                    <Typography variant='body2'>Technical Debt</Typography>
                    <Typography
                      variant='body2'
                      fontWeight='bold'
                      color='error.main'
                    >
                      {Math.round(data.effortDistribution?.technicalDebt || 0)}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant='determinate'
                    value={data.effortDistribution?.technicalDebt || 0}
                    color='error'
                  />
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent>
              <Typography variant='h6' gutterBottom>
                Feature Complexity Mix
              </Typography>
              <Stack spacing={1}>
                {data.tShirtSizeDistribution &&
                  Object.entries(data.tShirtSizeDistribution).map(
                    ([size, count]) => (
                      <Box
                        key={size}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <Chip
                          label={size}
                          size='small'
                          color={getTShirtSizeColor(size)}
                        />
                        <Typography variant='body2'>
                          {count} features (
                          {totalFeatures > 0
                            ? Math.round((count / totalFeatures) * 100)
                            : 0}
                          %)
                        </Typography>
                      </Box>
                    )
                  )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent>
              <Typography variant='h6' gutterBottom>
                Key Insights
              </Typography>
              <Stack spacing={2}>
                <Alert
                  severity={
                    (data.effortDistribution?.newDevelopment || 0) >= 60
                      ? 'success'
                      : 'info'
                  }
                >
                  New development is{' '}
                  {(data.effortDistribution?.newDevelopment || 0) >= 60
                    ? 'healthy'
                    : 'moderate'}{' '}
                  at {Math.round(data.effortDistribution?.newDevelopment || 0)}%
                </Alert>
                <Alert
                  severity={
                    (data.effortDistribution?.technicalDebt || 0) > 30
                      ? 'warning'
                      : 'success'
                  }
                >
                  Technical debt is{' '}
                  {(data.effortDistribution?.technicalDebt || 0) > 30
                    ? 'high'
                    : 'manageable'}{' '}
                  at {Math.round(data.effortDistribution?.technicalDebt || 0)}%
                </Alert>
                <Alert severity={totalFeatures > 0 ? 'success' : 'info'}>
                  {totalFeatures} total features across all complexity levels
                </Alert>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};
